import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X, FileText, Mail, Loader2 } from "lucide-react";
import { API } from "./utils";

export default function ManualImportModal({ webinar, token, onClose, onImported }) {
  const [mode, setMode] = useState("csv"); // 'csv' | 'text'
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "csv" && !file) {
      toast.error("Bitte zuerst eine CSV-Datei auswählen.");
      return;
    }
    if (mode === "text" && !text.trim()) {
      toast.error("Bitte zuerst Text einfügen.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      if (mode === "csv") fd.append("file", file);
      else fd.append("text", text);
      const r = await axios.post(
        `${API}/admin/zoho-webinar/import-registrants/${webinar.id}`,
        fd,
        { headers: { "X-Admin-Token": token } },
      );
      onImported(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      data-testid="manual-import-modal"
    >
      <form
        onSubmit={submit}
        className="bg-white rounded-2xl w-full max-w-xl p-6 shadow-2xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-lg flex items-center gap-2">
            <Upload className="h-5 w-5 text-[var(--al-purple)]" />
            Registranten manuell importieren
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            data-testid="manual-import-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-xs text-slate-600 mb-4 leading-relaxed">
          Fallback wenn die Zoho-API nicht antwortet. Lade entweder die CSV aus
          dem Zoho-„Registrierungsbericht" hoch <strong>oder</strong> füge die Liste
          als Text ein (eine Zeile pro Person).
        </p>

        <div className="flex items-center gap-2 mb-4">
          <button
            type="button"
            onClick={() => setMode("csv")}
            data-testid="manual-import-tab-csv"
            className={`flex-1 text-xs font-bold rounded-full px-3 py-2 border transition-all ${
              mode === "csv"
                ? "bg-[var(--al-purple)] text-white border-transparent"
                : "bg-white text-slate-600 border-slate-200 hover:border-violet-200"
            }`}
          >
            <FileText className="h-3.5 w-3.5 inline mr-1" /> CSV-Datei
          </button>
          <button
            type="button"
            onClick={() => setMode("text")}
            data-testid="manual-import-tab-text"
            className={`flex-1 text-xs font-bold rounded-full px-3 py-2 border transition-all ${
              mode === "text"
                ? "bg-[var(--al-purple)] text-white border-transparent"
                : "bg-white text-slate-600 border-slate-200 hover:border-violet-200"
            }`}
          >
            <Mail className="h-3.5 w-3.5 inline mr-1" /> Text einfügen
          </button>
        </div>

        {mode === "csv" ? (
          <div className="space-y-2 mb-4">
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="manual-import-file"
              className="block w-full text-xs text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-violet-50 file:text-[var(--al-purple)] hover:file:bg-violet-100"
            />
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Tipp: In Zoho Webinar → Berichte → Registrierungsbericht → Exportieren als CSV. Der Parser erkennt das Format automatisch.
            </p>
          </div>
        ) : (
          <div className="space-y-2 mb-4">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="manual-import-text"
              rows={10}
              placeholder={"Eine Person pro Zeile, z.B.:\nKai Boser\tkai.boser@web.de\nKristina Ruoss\tkristina.ruoss@gmx.ch\n…"}
              className="al-input font-mono text-xs"
            />
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Erkannt werden: Tab, Komma oder mehrere Leerzeichen zwischen Name und E-Mail. Datumsangaben am Ende werden ignoriert.
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="al-cta-ghost text-sm"
            data-testid="manual-import-cancel"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={busy}
            className="al-cta text-sm disabled:opacity-50"
            data-testid="manual-import-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            <span>Importieren</span>
          </button>
        </div>
      </form>
    </div>
  );
}
